import { useMutation, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import {
  parseToadMessageText,
  parseToadMessageObject,
  removeEmpty,
  useAdminParams,
} from 'utils/helpers';
import { ApiError } from 'utils/types';
import { withAcl } from 'hooks/Api';

import { CONTENTS, CONVERSATIONS } from '../utils/constants';

interface Payload {
  content: string;
  meta?: Record<string, any>;
  files?: File[];
  successCallback: (data: { _id: string; title: string }) => void;
}

export default function useCreateConversation() {
  const queryClient = useQueryClient();
  const { accountId, accountUserId } = useAdminParams();
  return useMutation<any, ApiError, Payload, any>({
    mutationFn: async ({ content, meta }) => {
      const token = Cookies.get('_token');
      const { url } = await withAcl({
        account: {
          url: 'api/v1/channels/toad/conversations/stream',
        },
        admin: {
          // eslint-disable-next-line max-len
          url: `api/v2/accounts/${accountId}/users/${accountUserId}/channels/toad/conversations/stream`,
        },
      });

      const data = await fetch(`${import.meta.env.VITE_APP_END_POINT}/${url}`, {
        method: 'POST',
        body: JSON.stringify(removeEmpty({ content, meta, role: 'user' })),
        headers: [
          ['Accepts', 'application/json, text/plain, */*'],
          ['Content-Type', 'application/json'],
          ['Access-Control-Allow-Origin', '*'],
          ['Access-Control-Allow-Credentials', 'true'],
          ['Authorization', `Bearer ${token}`],
        ],
      });

      return data.body;
    },
    onSuccess: async (data, payload) => {
      const reader = data?.getReader();
      const decoder = new TextDecoder();
      let botMessage = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (reader) {
          const { done, value } = await reader.read();
          if (done) break;
          const decodedMessage = decoder.decode(value, { stream: true });
          if (decodedMessage) botMessage += decodedMessage;

          const newConversation = parseToadMessageObject(botMessage);

          const oldData = queryClient.getQueryData([
            CONVERSATIONS,
            newConversation?._id,
            CONTENTS,
          ]);

          if (oldData) {
            queryClient.setQueryData(
              [CONVERSATIONS, newConversation?._id, CONTENTS],
              (prev: any) => {
                const _pages = [...prev];

                const messageIndex = _pages.findIndex(
                  (p: any) => p._id === payload.content,
                );

                _pages[messageIndex] = {
                  _id: payload.content,
                  content: parseToadMessageText(botMessage),
                  role: 'assistant',
                  toadConversationId: newConversation?._id,
                };

                return _pages;
              },
            );
          } else {
            payload.successCallback({
              _id: newConversation._id,
              title: newConversation.title,
            });
            queryClient.setQueryData(
              [CONVERSATIONS, { keyword: '' }],
              (prev: any) => {
                return (prev ?? []).concat(newConversation);
              },
            );

            queryClient.setQueryData(
              [CONVERSATIONS, newConversation._id, CONTENTS],
              () => {
                const _pages = [];

                _pages.push({
                  _id: new Date().toISOString(),
                  content: payload.content,
                  role: 'user',
                  toadConversationId: newConversation._id,
                });

                _pages.push({
                  _id: payload.content,
                  content: parseToadMessageText(botMessage),
                  role: 'assistant',
                  toadConversationId: newConversation?._id,
                });

                return _pages;
              },
            );
          }

          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth',
          });
        }
      }
      // toastSuccess('Successfully created new tag.');
    },
  });
}
